import { useId, useMemo, useState } from 'react';

/**
 * 여러 계열을 겹쳐 그리는 선 그래프.
 *
 * 원본 통계 화면은 Chart.js(CDN) 를 썼다. 여기서 필요한 건 선 그래프 하나뿐이라
 * 라이브러리를 들이지 않고 SVG 로 직접 그린다. 번들이 커지지 않는다.
 */

export interface Series {
  id: string;
  label: string;
  /** x 는 라벨(날짜), y 는 값 */
  points: { x: string; y: number }[];
}

interface Props {
  series: Series[];
  /** x 축 전체 라벨. 계열마다 값이 없는 날은 끊어 그린다. */
  labels: string[];
  height?: number;
  /** 값 포맷 (툴팁·y축) */
  format?: (n: number) => string;
}

/**
 * 계열 색. 겹쳐도 구분되도록 색상환을 돌린다. styles.css :root 의 테마 토큰을
 * 그대로 참조해서 - 리터럴 hex 를 여기 따로 안 둬서 - 야간/주간 모드에 맞춰
 * 같이 바뀐다.
 */
const COLORS = [
  'var(--accent)', 'var(--stat-orange)', 'var(--stat-green)', 'var(--chart-pink)',
  'var(--stat-purple-strong)', 'var(--chart-coral)', 'var(--chart-teal)', 'var(--chart-yellow)',
];

const PAD = { top: 12, right: 14, bottom: 26, left: 56 };

export function LineChart({ series, labels, height = 260, format = (n) => n.toLocaleString() }: Props) {
  const gradId = useId();
  const [hidden, setHidden] = useState<Set<string>>(new Set());
  const [hover, setHover] = useState<number | null>(null);

  const shown = series.filter((s) => !hidden.has(s.id));

  const { max, width, innerW, innerH, xAt, yAt } = useMemo(() => {
    const width = 900;
    const innerW = width - PAD.left - PAD.right;
    const innerH = height - PAD.top - PAD.bottom;
    const values = shown.flatMap((s) => s.points.map((p) => p.y));
    const rawMax = values.length ? Math.max(...values) : 0;
    // 눈금이 어중간한 숫자로 끝나지 않게 위로 올림한다.
    const step = Math.max(1, Math.pow(10, Math.floor(Math.log10(rawMax || 1))) / 2);
    const max = Math.max(step, Math.ceil((rawMax || 1) / step) * step);
    const xAt = (i: number) =>
      PAD.left + (labels.length <= 1 ? innerW / 2 : (innerW * i) / (labels.length - 1));
    const yAt = (v: number) => PAD.top + innerH - (innerH * v) / max;
    return { max, width, innerW, innerH, xAt, yAt };
  }, [shown, labels.length, height]);

  if (labels.length === 0) {
    return <p className="chart-empty">그릴 자료가 없습니다.</p>;
  }

  const indexOf = new Map(labels.map((l, i) => [l, i]));
  const ticks = [0, 0.25, 0.5, 0.75, 1].map((r) => max * r);

  // x 축 라벨은 너무 촘촘하면 겹친다. 대략 8개만 남긴다.
  const labelEvery = Math.max(1, Math.ceil(labels.length / 8));

  function toggle(id: string) {
    setHidden((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  return (
    <div className="chart">
      <svg
        className="chart__svg"
        viewBox={`0 0 ${width} ${height}`}
        role="img"
        aria-label={`일별 추이 그래프. 계열 ${shown.length}개.`}
        onMouseLeave={() => setHover(null)}
      >
        <defs>
          <clipPath id={gradId}>
            <rect x={PAD.left} y={PAD.top} width={innerW} height={innerH} />
          </clipPath>
        </defs>

        {ticks.map((t) => (
          <g key={t}>
            <line
              x1={PAD.left}
              x2={width - PAD.right}
              y1={yAt(t)}
              y2={yAt(t)}
              className="chart__grid"
            />
            <text x={PAD.left - 8} y={yAt(t) + 4} className="chart__ytick">
              {format(Math.round(t))}
            </text>
          </g>
        ))}

        {labels.map((l, i) =>
          i % labelEvery === 0 ? (
            <text key={l} x={xAt(i)} y={height - 8} className="chart__xtick">
              {l.slice(5)}
            </text>
          ) : null,
        )}

        <g clipPath={`url(#${gradId})`}>
          {shown.map((s, si) => {
            const color = COLORS[series.findIndex((x) => x.id === s.id) % COLORS.length];
            // 값이 없는 날은 잇지 않는다. 0 으로 이으면 없던 골짜기가 생긴다.
            const d = s.points
              .map((p, idx) => {
                const i = indexOf.get(p.x);
                if (i === undefined) return '';
                const prev = s.points[idx - 1];
                const gap = !prev || (indexOf.get(prev.x) ?? -9) !== i - 1;
                return `${gap ? 'M' : 'L'}${xAt(i).toFixed(1)},${yAt(p.y).toFixed(1)}`;
              })
              .join(' ');
            return <path key={s.id} d={d} className="chart__line" stroke={color} data-series={si} />;
          })}
        </g>

        {hover !== null && (
          <line
            x1={xAt(hover)}
            x2={xAt(hover)}
            y1={PAD.top}
            y2={PAD.top + innerH}
            className="chart__cursor"
          />
        )}

        {/* 마우스를 받는 투명 칸. 각 x 위치마다 하나씩. */}
        {labels.map((l, i) => (
          <rect
            key={l}
            x={xAt(i) - innerW / Math.max(1, labels.length) / 2}
            y={PAD.top}
            width={Math.max(2, innerW / Math.max(1, labels.length))}
            height={innerH}
            fill="transparent"
            onMouseEnter={() => setHover(i)}
          />
        ))}
      </svg>

      {hover !== null && (
        <div className="chart__tip">
          <strong>{labels[hover]}</strong>
          {shown
            .map((s) => ({ s, p: s.points.find((p) => p.x === labels[hover]) }))
            .filter((x) => x.p)
            .map(({ s, p }) => (
              <span key={s.id}>
                <i
                  className="chart__dot"
                  style={{
                    background: COLORS[series.findIndex((x) => x.id === s.id) % COLORS.length],
                  }}
                />
                {s.label} {format(p!.y)}
              </span>
            ))}
        </div>
      )}

      <div className="chart__legend">
        {series.map((s, i) => (
          <button
            key={s.id}
            type="button"
            className={hidden.has(s.id) ? 'chart__key chart__key--off' : 'chart__key'}
            onClick={() => toggle(s.id)}
            aria-pressed={!hidden.has(s.id)}
          >
            <i className="chart__dot" style={{ background: COLORS[i % COLORS.length] }} />
            {s.label}
          </button>
        ))}
      </div>
    </div>
  );
}
